import { type CurrentUserIguanaObject, type CurrentUser, isCurrentUser } from 'Users';
import { type StreamIguanaObject, type StreamForDashboard } from 'Lessons';
import { transformKeyCase } from 'Utils/transformKeyCase';
import { VerificationMethod, type ExamVerification } from './ExamVerification.types';
import { getOrGenerateExamVerificationSessionId } from './getOrGenerateExamVerificationSessionId';

function getExamVerifications(user: CurrentUserIguanaObject | CurrentUser): ExamVerification[] {
    if (isCurrentUser(user)) {
        return user?.examVerifications ?? [];
    }

    const iguanaExamVerifications = user?.exam_verifications ?? [];

    return iguanaExamVerifications.map(examVerification =>
        transformKeyCase<ExamVerification>(examVerification, { to: 'camelCase' }),
    );
}

// Gets an ExamVerification that matches the current Stream/ExamVerificationSession. Can
// be either a BioSig verification, or a previously claimed admin verification.
export function examVerificationRecordForExamVerificationSession(
    user: CurrentUserIguanaObject | CurrentUser,
    stream: StreamForDashboard | StreamIguanaObject,
) {
    return getExamVerifications(user).find(
        ev =>
            // Technically, this check on locale_pack_id isn't strictly needed here as we generate a unique
            // ExamVerificationSession for each exam Stream.
            ev.streamLocalePackId === stream.localePackId &&
            ev.examVerificationSessionId === getOrGenerateExamVerificationSessionId(stream),
    );
}

// Gets an unclaimed admin ExamVerification that matches the Stream's locale_pack_id.
export function unclaimedAdminExamVerificationRecordForStream(
    user: CurrentUser | CurrentUserIguanaObject,
    stream: StreamForDashboard | StreamIguanaObject,
) {
    return getExamVerifications(user).find(
        ev =>
            ev.verificationMethod === VerificationMethod.Admin &&
            ev.streamLocalePackId === stream.localePackId &&
            ev.examVerificationSessionId === null,
    );
}

export function examVerificationRecordForStream(
    user: CurrentUserIguanaObject | CurrentUser,
    stream: StreamForDashboard | StreamIguanaObject,
): ExamVerification | undefined {
    return (
        // Order is important here because we claim unclaimed admin ExamVerification records to launch exams.
        // The presence of an ExamVerification corresponding to the Stream's ExamVerificationSessionId on this device
        // implies that the user was able to successfully verify their identity on this device, either through BioSig
        // or by previously claiming an unclaimed admin ExamVerification.
        // We should always give an ExamVerification corresponding to the ExamVerificationSessionId preference so we don't
        // accidentally consume an admin ExamVerification on a device where it's not required.
        examVerificationRecordForExamVerificationSession(user, stream) ||
        unclaimedAdminExamVerificationRecordForStream(user, stream)
    );
}
